// Field config for config-driven CRUD modules
// types: text, number, money, date, select, textarea, ref

export const MODULES = {
  properties: {
    title: "Properties", singular: "Property", icon: "Building2",
    columns: ["name", "type", "units", "market_value"],
    labels: { name: "Name", type: "Type", units: "Units", market_value: "Market Value" },
    fields: [
      { key: "name", label: "Property Name", type: "text", required: true },
      { key: "address", label: "Address", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "county", label: "County", type: "text" },
      { key: "state", label: "State", type: "text" },
      { key: "zip", label: "ZIP Code", type: "text" },
      { key: "parcel_id", label: "Parcel / Property ID", type: "text" },
      { key: "owner", label: "Owner of Record", type: "text" },
      { key: "type", label: "Property Type", type: "select", options: ["Single Family", "Condo", "Townhouse", "Duplex", "Triplex", "Fourplex", "Multi-Family", "Commercial", "Mixed-Use"] },
      { key: "units", label: "Number of Units", type: "number" },
      { key: "year_built", label: "Year Built", type: "number" },
      { key: "rental_type", label: "Rental Type", type: "select", options: ["Long-Term Rental", "Short-Term Rental"] },
      { key: "ownership_type", label: "Ownership Type", type: "select", options: ["Personal Name", "LLC", "Corporation", "Trust"] },
      { key: "purchase_price", label: "Purchase Price", type: "money" },
      { key: "purchase_date", label: "Purchase Date", type: "date" },
      { key: "market_value", label: "Current Market Value", type: "money" },
      { key: "mortgage_lender", label: "Mortgage Lender", type: "text" },
      { key: "mortgage_balance", label: "Mortgage Balance", type: "money" },
      { key: "mortgage_payment", label: "Monthly Mortgage", type: "money" },
      { key: "hoa_name", label: "HOA Name", type: "text" },
      { key: "hoa_fee", label: "Monthly HOA Fee", type: "money" },
      { key: "photo", label: "Photo URL", type: "text" },
      { key: "notes", label: "Notes", type: "textarea" },
      { key: "_compliance_header", label: "Compliance Profile (regulated features)", type: "section" },
      { key: "has_pool", label: "Has Pool", type: "select", options: ["No", "Yes"] },
      { key: "has_laundry", label: "Has Laundry Machines", type: "select", options: ["No", "Yes"] },
      { key: "has_elevator", label: "Has Elevator", type: "select", options: ["No", "Yes"] },
      { key: "has_fire_system", label: "Has Fire System", type: "select", options: ["No", "Yes"] },
      { key: "_welcome_header", label: "Welcome Packet Info (for tenant move-in)", type: "section" },
      { key: "utility_electric", label: "Electricity Company", type: "text" },
      { key: "utility_water", label: "Water Company", type: "text" },
      { key: "utility_gas", label: "Gas Company", type: "text" },
      { key: "internet_provider", label: "Internet / Cable Provider", type: "text" },
      { key: "trash_days", label: "Trash Pickup Days", type: "text" },
      { key: "recycling_info", label: "Recycling Info", type: "text" },
      { key: "lawn_info", label: "Lawn / Landscaping", type: "text" },
      { key: "pool_info", label: "Pool Service", type: "text" },
      { key: "emergency_contact", label: "Emergency Contact", type: "text" },
      { key: "rent_instructions", label: "Rent Payment Instructions", type: "textarea" },
      { key: "property_rules", label: "Property Rules / House Rules", type: "textarea" },
    ],
  },
  units: {
    title: "Units", singular: "Unit", icon: "DoorOpen",
    columns: ["unit_number", "property_name", "tenant_name", "rent_amount", "status"],
    labels: { unit_number: "Unit", property_name: "Property", tenant_name: "Tenant", rent_amount: "Rent", status: "Status" },
    badge: { field: "status", map: { Occupied: "ok", Vacant: "danger" } },
    fields: [
      { key: "unit_number", label: "Unit Number", type: "text", required: true },
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "tenant_name", label: "Current Tenant", type: "text" },
      { key: "rent_amount", label: "Rent Amount", type: "money" },
      { key: "security_deposit", label: "Security Deposit", type: "money" },
      { key: "status", label: "Status", type: "select", options: ["Occupied", "Vacant"] },
      { key: "utility_responsibility", label: "Utility Responsibility", type: "select", options: ["Tenant", "Owner", "Split"] },
      { key: "parking", label: "Parking Information", type: "text" },
      { key: "appliances", label: "Appliance Inventory", type: "textarea" },
    ],
  },
  tenants: {
    title: "Tenants", singular: "Tenant", icon: "Users",
    columns: ["name", "property_name", "unit", "status"],
    labels: { name: "Name", property_name: "Property", unit: "Unit", status: "Status" },
    badge: { field: "status", map: { Active: "ok", "Future Tenant": "info", Vacated: "muted" } },
    fields: [
      { key: "name", label: "Full Name", type: "text", required: true },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "employer", label: "Employer", type: "text" },
      { key: "dob", label: "Date of Birth", type: "date" },
      { key: "emergency_contact", label: "Emergency Contact", type: "text" },
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "unit", label: "Unit Number", type: "text" },
      { key: "move_in", label: "Move-In Date", type: "date" },
      { key: "move_out", label: "Move-Out Date", type: "date" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Future Tenant", "Vacated"] },
      { key: "_comm_header", label: "Communication Preferences", type: "section" },
      { key: "can_email", label: "Can Receive Email", type: "select", options: ["Yes", "No"] },
      { key: "can_sms", label: "Can Receive SMS", type: "select", options: ["Yes", "No"] },
      { key: "preferred_method", label: "Preferred Method", type: "select", options: ["Email", "SMS", "Both"] },
      { key: "opted_out", label: "Opted Out of SMS", type: "select", options: ["No", "Yes"] },
    ],
  },
  leases: {
    title: "Leases", singular: "Lease", icon: "FileText",
    columns: ["tenant_name", "property_name", "monthly_rent", "end_date", "status"],
    labels: { tenant_name: "Tenant", property_name: "Property", monthly_rent: "Rent", end_date: "Ends", status: "Status" },
    badge: { field: "status", map: { Active: "ok", Expired: "danger", Pending: "info" } },
    fields: [
      { key: "tenant_name", label: "Tenant", type: "ref", ref: "tenants", refLabel: "name", setId: "tenant_id" },
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "unit", label: "Unit", type: "text" },
      { key: "start_date", label: "Lease Start Date", type: "date" },
      { key: "end_date", label: "Lease End Date", type: "date" },
      { key: "monthly_rent", label: "Monthly Rent", type: "money" },
      { key: "security_deposit", label: "Security Deposit", type: "money" },
      { key: "deposit_refunded", label: "Deposit Refunded", type: "money" },
      { key: "deposit_withheld", label: "Deposit Withheld", type: "money" },
      { key: "deposit_status", label: "Deposit Status", type: "select", options: ["Held", "Partially Refunded", "Fully Refunded", "Withheld", "Forfeited"] },
      { key: "deposit_notes", label: "Deposit Notes", type: "text" },
      { key: "late_fee", label: "Late Fee", type: "money" },
      { key: "grace_period", label: "Grace Period (days)", type: "number" },
      { key: "renewal_option", label: "Renewal Option", type: "select", options: ["Yes", "No", "Month-to-Month"] },
      { key: "status", label: "Lease Status", type: "select", options: ["Active", "Pending", "Expired"] },
    ],
  },
  payments: {
    title: "Rent Collection", singular: "Payment", icon: "DollarSign",
    columns: ["tenant_name", "property_name", "amount_due", "amount_paid", "payment_date", "status"],
    labels: { tenant_name: "Tenant", property_name: "Property", amount_due: "Due", amount_paid: "Paid", payment_date: "Date", status: "Status" },
    badge: { field: "status", map: { Paid: "ok", Delinquent: "danger", Late: "danger", Unpaid: "danger", Partial: "info" } },
    fields: [
      { key: "tenant_name", label: "Tenant", type: "ref", ref: "tenants", refLabel: "name", setId: "tenant_id" },
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "amount_due", label: "Amount Due", type: "money" },
      { key: "amount_paid", label: "Amount Paid", type: "money" },
      { key: "outstanding", label: "Outstanding Balance", type: "money" },
      { key: "payment_date", label: "Payment Date", type: "date" },
      { key: "method", label: "Payment Method", type: "select", options: ["Zelle", "Bank Transfer", "Credit Card", "Check", "Cash"] },
      { key: "late_fee", label: "Late Fee", type: "money" },
      { key: "status", label: "Status", type: "select", options: ["Paid", "Partial", "Late", "Unpaid", "Delinquent"] },
    ],
  },
  maintenance: {
    title: "Maintenance", singular: "Work Order", icon: "Wrench",
    columns: ["issue", "property_name", "priority", "estimated_cost", "status"],
    labels: { issue: "Issue", property_name: "Property", priority: "Priority", estimated_cost: "Est. Cost", status: "Status" },
    badge: { field: "status", map: { Completed: "ok", New: "danger", Open: "danger", "In Progress": "info", "Waiting on Contractor": "info", Cancelled: "muted", Closed: "muted" } },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "unit", label: "Unit", type: "text" },
      { key: "tenant_name", label: "Tenant", type: "text" },
      { key: "issue", label: "Issue Description", type: "textarea", required: true },
      { key: "priority", label: "Priority", type: "select", options: ["Low", "Medium", "High", "Urgent"] },
      { key: "contractor", label: "Assigned Contractor", type: "ref", ref: "contractors", refLabel: "company", setId: "contractor_id" },
      { key: "scheduled_date", label: "Scheduled Date", type: "date" },
      { key: "estimated_cost", label: "Estimated Cost", type: "money" },
      { key: "actual_cost", label: "Actual Cost", type: "money" },
      { key: "invoice_number", label: "Invoice Number", type: "text" },
      { key: "warranty", label: "Warranty Information", type: "text" },
      { key: "status", label: "Status", type: "select", options: ["New", "In Progress", "Waiting on Contractor", "Completed", "Cancelled"] },
    ],
  },
  contractors: {
    title: "Contractors", singular: "Contractor", icon: "HardHat",
    columns: ["company", "category", "name", "phone", "rating"],
    labels: { company: "Company", category: "Category", name: "Contact", phone: "Phone", rating: "Rating" },
    fields: [
      { key: "company", label: "Company", type: "text", required: true },
      { key: "name", label: "Contact Name", type: "text" },
      { key: "category", label: "Category", type: "select", options: ["Electrician", "Plumber", "HVAC", "Roofer", "Painter", "Landscaper", "Pest Control", "Pool Maintenance", "Cleaning", "General Contractor"] },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "license_number", label: "License Number", type: "text" },
      { key: "rating", label: "Rating", type: "number" },
      { key: "jobs_completed", label: "Jobs Completed", type: "number" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  insurance: {
    title: "Insurance", singular: "Policy", icon: "ShieldCheck",
    columns: ["property_name", "company", "policy_number", "annual_premium", "renewal_date"],
    labels: { property_name: "Property", company: "Company", policy_number: "Policy #", annual_premium: "Premium", renewal_date: "Renewal" },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "company", label: "Insurance Company", type: "text" },
      { key: "agent", label: "Agent Information", type: "text" },
      { key: "policy_number", label: "Policy Number", type: "text" },
      { key: "coverage_amount", label: "Coverage Amount", type: "money" },
      { key: "deductible", label: "Deductible", type: "money" },
      { key: "annual_premium", label: "Annual Premium", type: "money" },
      { key: "renewal_date", label: "Renewal Date", type: "date" },
      { key: "payment_schedule", label: "Payment Schedule", type: "select", options: ["Annual", "Semi-Annual", "Quarterly", "Monthly"] },
    ],
  },
  taxes: {
    title: "Property Taxes", singular: "Tax Record", icon: "Landmark",
    columns: ["property_name", "county", "account_number", "amount", "due_date", "status"],
    labels: { property_name: "Property", county: "County", account_number: "Folio #", amount: "Amount", due_date: "Due", status: "Status" },
    badge: { field: "status", map: { Paid: "ok", Pending: "info", Overdue: "danger" } },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "county", label: "County", type: "text" },
      { key: "account_number", label: "Folio / Account Number", type: "text" },
      { key: "parcel_number", label: "Parcel Number", type: "text" },
      { key: "amount", label: "Tax Bill Amount", type: "money" },
      { key: "discount_date", label: "Early Payment Discount Date", type: "date" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "status", label: "Payment Status", type: "select", options: ["Pending", "Paid", "Overdue"] },
    ],
  },
  mortgages: {
    title: "Mortgages", singular: "Mortgage", icon: "Banknote",
    columns: ["property_name", "bank", "current_balance", "monthly_payment", "due_date"],
    labels: { property_name: "Property", bank: "Bank", current_balance: "Balance", monthly_payment: "Payment", due_date: "Due" },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "bank", label: "Bank Name", type: "text" },
      { key: "loan_number", label: "Loan Number", type: "text" },
      { key: "original_amount", label: "Original Loan Amount", type: "money" },
      { key: "current_balance", label: "Current Balance", type: "money" },
      { key: "interest_rate", label: "Interest Rate (%)", type: "number" },
      { key: "loan_type", label: "Loan Type", type: "select", options: ["30-Yr Fixed", "15-Yr Fixed", "ARM", "Interest-Only"] },
      { key: "monthly_payment", label: "Monthly Payment", type: "money" },
      { key: "escrow", label: "Escrow Amount", type: "money" },
      { key: "due_date", label: "Next Due Date", type: "date" },
      { key: "maturity_date", label: "Maturity Date", type: "date" },
    ],
  },
  hoa: {
    title: "HOA", singular: "HOA Account", icon: "Home",
    columns: ["property_name", "company", "monthly_fee", "due_date", "violations"],
    labels: { property_name: "Property", company: "HOA Company", monthly_fee: "Fee", due_date: "Due", violations: "Violations" },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "company", label: "HOA Company", type: "text" },
      { key: "contact", label: "Contact Person", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "email", label: "Email", type: "text" },
      { key: "monthly_fee", label: "Monthly Fee", type: "money" },
      { key: "special_assessment", label: "Special Assessment", type: "money" },
      { key: "violations", label: "Violations", type: "text" },
      { key: "due_date", label: "Due Date", type: "date" },
    ],
  },
  utilities: {
    title: "Utilities", singular: "Utility Account", icon: "Plug",
    columns: ["utility_type", "property_name", "provider", "amount_due", "due_date", "status"],
    labels: { utility_type: "Type", property_name: "Property", provider: "Provider", amount_due: "Amount", due_date: "Due", status: "Status" },
    badge: { field: "status", map: { Paid: "ok", Pending: "info", Overdue: "danger" } },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "utility_type", label: "Utility", type: "select", options: ["Water", "Electricity", "Gas", "Internet", "Trash", "Sewer", "Landscaping", "Pest Control", "Pool Maintenance"] },
      { key: "provider", label: "Provider", type: "text" },
      { key: "account_number", label: "Account Number", type: "text" },
      { key: "amount_due", label: "Amount Due", type: "money" },
      { key: "due_date", label: "Due Date", type: "date" },
      { key: "status", label: "Payment Status", type: "select", options: ["Pending", "Paid", "Overdue"] },
    ],
  },
  expenses: {
    title: "Expenses", singular: "Expense", icon: "Receipt",
    columns: ["category", "property_name", "amount", "date"],
    labels: { category: "Category", property_name: "Property", amount: "Amount", date: "Date" },
    fields: [
      { key: "property_name", label: "Property", type: "ref", ref: "properties", refLabel: "name", setId: "property_id" },
      { key: "category", label: "Category", type: "select", options: ["Mortgage", "HOA", "Insurance", "Property Taxes", "Utilities", "Water", "Electricity", "Gas", "Trash", "Sewer", "Landscaping", "Pest Control", "Repairs", "Legal Fees", "Bank Fees", "Other"] },
      { key: "amount", label: "Amount", type: "money" },
      { key: "date", label: "Date", type: "date" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
};
